import { Inject, Injectable } from '@angular/core'
import { Message } from '@modules/messages/models'
import { RUNTIME_CONFIG, RuntimeConfig } from '@config/runtime-config'
import { BehaviorSubject, Subscription, firstValueFrom } from 'rxjs'
import { AccountService } from '@services/account.service'
import { NotificationService } from '@services/notification.service'
import { getToken, onMessage } from 'firebase/messaging'
import { FirebaseMessagingService } from '@services/firebase-messaging.service'
import { NotificationPreference, NotificationType } from '@models/notification.model'
import { ToastService } from '@common/services'
import { ChatMessage, ChatWsEvent } from '@models/chat/chat.model'
import { ChatNotificationSocketService } from '@services/chat-notification-socket.service'
import moment from 'moment/moment'
import { PlatformService } from '@services/platform.service'
import { SupplierService } from '@services/supplier.service'
import { AuthService } from '@services/auth.service'
import { CHAT_TRIGGERED_BY, triggeredByMe } from '@utils/ws-notification.util'

/**
 * Tipos de evento (metadata `NOTIFICATION_TYPE`) que já disparam uma notificação direcional própria
 * (ver `SupplierNotificationSocketService`) — quando uma mensagem de chat corresponde a um desses e o
 * `SUPPLIER_ID` bate com o fornecedor logado, o toast é destacado como "Ação necessária" em vez do
 * genérico "Nova mensagem", já que fica claro que é uma ação do próprio fornecedor, não apenas tráfego
 * do chat da sessão.
 */
const TARGETED_NOTIFICATION_TYPES = new Set([
	'supplier_enrollment_notice_proposal_appropriate',
	'supplier_enrollment_notice_trading_opened',
	'supplier_enrollment_notice_trading_closed',
	'supplier_enrollment_notice_feasibility_opened',
	'supplier_enrollment_notice_qualification_approved',
	'supplier_enrollment_notice_approved',
	'supplier_enrollment_notice_adjusted_opened',
	'supplier_enrollment_notice_adjusted_closed'
])

@Injectable({
	providedIn: 'root'
})
export class MessagesService {
	disabled = false
	messages = new Array<Message>()
	messageSource = new BehaviorSubject<Message>(null)
	private chatNotificationSub?: Subscription

	private mySupplierId: string | null | undefined = undefined

	constructor(
		private toast: ToastService,
		private firebaseMessaging: FirebaseMessagingService,
		private platform: PlatformService,
		private accountService: AccountService,
		private supplierService: SupplierService,
		private notificationService: NotificationService,
		private chatNotificationSocket: ChatNotificationSocketService,
		@Inject(RUNTIME_CONFIG) private runtimeConfig: RuntimeConfig,
		private authService: AuthService
	) {}

	async isGranted() {
		try {
			return Notification.permission === 'granted'
		} catch (e) {
			console.error('Erro ao solicitar permissão para notificações')
			return false
		}
	}

	async requestPermission() {
		try {
			if (Notification.permission === 'granted') {
				await this.registerPushNotifications()
				return
			}

			if (Notification.permission === 'denied') {
				return
			}

			const permission = await Notification.requestPermission()

			if (permission !== 'granted') {
				return
			}

			await this.registerPushNotifications()
		} catch (e) {
			console.warn('Erro ao solicitar permissão para notificações')
		}
	}

	async registerPushNotifications() {
		try {
			const granted = await this.isGranted()
			const verify = localStorage.getItem('push_verify')

			if (!granted && !/true/.test(verify) && !this.platform.mobile) {
				this.toast.info('Atenção', 'Ative as notificações.', {
					closeButton: true,
					disableTimeOut: true,
					positionClass: 'toast-bottom-left'
				})

				this.toast.toastrTemp.onHidden.subscribe(async () => {
					console.debug('toast notify onHidden')
					localStorage.setItem('push_verify', 'true')
					await this.requestPermission()
				})
			}

			const registration = await navigator.serviceWorker.register('/assets/firebase-messaging-sw.js')

			const token = await getToken(this.firebaseMessaging.messaging, {
				vapidKey: this.runtimeConfig.vapid_key,
				serviceWorkerRegistration: registration
			})

			const device = token.toString()
			const preferences = await firstValueFrom(this.notificationService.index())

			if (!preferences.some(it => it.target === device && it.type === NotificationType.PUSH)) {
				const tenantId = this.accountService.account && this.accountService.account.organization ? this.accountService.account.tenantId : null
				await firstValueFrom(this.notificationService.save(NotificationPreference.ofPush(device, tenantId, true)))
			}
		} catch (e) {
			console.debug(e)
			console.warn('Não foi possível obter o token de notificações')
		}
	}

	receiveMessages() {
		if (this.firebaseMessaging.messaging) {
			onMessage(this.firebaseMessaging.messaging, payload => {
				const data = payload.data
				const body = data['pinpoint.notification.body']
				const title = data['pinpoint.notification.title']
				const jsonBody = data['pinpoint.jsonBody']

				const message = {
					text: title,
					details: body,
					color: 'text-success',
					icon: 'clipboard-check',
					data: jsonBody ? JSON.parse(jsonBody) : null
				}

				this.messageSource.next(message)
				this.messages.push(message)
				this.disabled = false
			})
		}
	}

	listenChatNotifications() {
		if (this.chatNotificationSub) return

		this.chatNotificationSocket.connect()
		this.chatNotificationSub = this.chatNotificationSocket.notifications$.subscribe(event => this.handleChatNotification(event))
	}

	async disconnectChatNotifications() {
		this.chatNotificationSub?.unsubscribe()
		this.chatNotificationSub = undefined
		await this.chatNotificationSocket.disconnect()
	}

	private async handleChatNotification(event: ChatWsEvent) {
		if (!event || event.type !== 'CHAT_MESSAGE_NOTIFICATION') return

		const payload = event.payload as ChatMessage
		const senderName = this.senderLabel(payload)
		const content = this.sanitizerContent(payload)
		const targeted = await this.isTargetedAtMe(payload)

		const message: Message = {
			text: senderName,
			details: content,
			color: targeted ? 'text-warning' : 'text-info',
			icon: targeted ? 'bell' : 'comments',
			data: payload as any
		}

		this.messageSource.next(message)
		this.messages.push(message)
		this.disabled = false

		// Mensagem de sistema narrando o ato do próprio usuário (ex.: o agente que habilitou o licitante):
		// ele acabou de fazer, o toast só repetiria. Segue na lista de notificações.
		if (triggeredByMe(payload.metadata?.['data']?.[CHAT_TRIGGERED_BY], this.authService?.token)) return

		this.toast.notification(`${content ?? ''}`, targeted ? 'Ação necessária' : 'Nova mensagem', {
			timeOut: 10000,
			enableHtml: true,
			closeButton: true,
			progressBar: true,
			positionClass: 'toast-top-left'
		})
	}

	/**
	 * Uma mensagem de chat é enviada a todos os participantes da sessão (agente + fornecedores), mas
	 * só faz sentido destacá-la como "ação necessária" para o fornecedor que ela realmente diz respeito.
	 * Compara `SUPPLIER_ID` (metadata do evento, ver `NoticeSessionEvent.toChatMessage()`) com o
	 * fornecedor logado — estável independente de qual sessão/inscrição a mensagem pertence, ao
	 * contrário do `enrollmentId` (um fornecedor pode ter várias inscrições/sessões ativas).
	 */
	private async isTargetedAtMe(payload: ChatMessage): Promise<boolean> {
		const data = payload.metadata?.['data'] ?? {}
		const type = data['NOTIFICATION_TYPE']
		const supplierId = data['SUPPLIER_ID']

		if (!type || !supplierId || !TARGETED_NOTIFICATION_TYPES.has(type)) {
			return false
		}

		const mySupplierId = await this.resolveMySupplierId()

		return !!mySupplierId && mySupplierId === supplierId
	}

	private async resolveMySupplierId(): Promise<string | null> {
		if (this.mySupplierId !== undefined) {
			return this.mySupplierId
		}

		try {
			const supplier = await firstValueFrom(this.supplierService.detailsSupplierInSession())
			this.mySupplierId = supplier?.id ?? null
		} catch {
			this.mySupplierId = null
		}

		return this.mySupplierId
	}

	private senderLabel(payload: ChatMessage): string {
		if (payload?.sender?.role === 1) {
			return 'Agente de Contratação'
		}
		return payload?.sender?.name ?? ''
	}

	private sanitizerContent(payload: ChatMessage): string {
		// O servidor manda a descrição COMPLETA dos itens e lotes, que a Ata precisa (15/10/2026); no
		// aviso entra o mesmo recorte do chat, por alvo.
		let content = ChatMessage.chatTargetLines(payload?.content ?? '')
		const data = payload.metadata?.['data'] ?? ({} as any)
		const details = data?.['DETAILS'] ?? ''
		const deadline = data?.['DEADLINE'] ?? null
		const agentName = data?.['AGENT_NAME'] ?? 'Agente'
		const supplierName = data?.['SUPPLIER_NAME'] ?? ''
		const supplierNicknameName = data?.['SUPPLIER_NICKNAME_NAME'] ?? ''
		// `DESCRIPTION`/`JUSTIFICATION` faltavam aqui (correção de 2026-09-22): os eventos que os usam
		// — desclassificação do remanescente, dispensa da re-especificação, entre outros — chegavam ao
		// toast com o placeholder CRU na tela. O chat (`ChatMessage.handlerContent`) já os trata; esta
		// cópia da substituição tinha ficado para trás.
		const description = ChatMessage.chatTargetsLabel(data?.['DESCRIPTION'])
		const justification = data?.['JUSTIFICATION'] ?? ''

		content = content.replaceAll('{DETAILS}', details)
		content = content.replaceAll('{AGENT_NAME}', agentName)
		content = content.replaceAll('{SUPPLIER_NAME}', supplierName)
		content = content.replaceAll('{SUPPLIER_NICKNAME_NAME}', supplierNicknameName)
		content = content.replaceAll('{DESCRIPTION}', description)
		content = content.replaceAll('{JUSTIFICATION}', justification)

		if (deadline) {
			content = content.replaceAll('{DEADLINE}', moment(deadline).format('DD/MM/YYYY HH:mm'))
		} else {
			content = content.replaceAll('{DEADLINE}', '')
		}

		return content
	}

	get total() {
		return this.messages.length ?? 0
	}
}
